import dayjs from 'dayjs'
import { Routes, Route, Navigate } from 'react-router-dom'

import { RoutesDashboard, ContractsDashboard } from '@components'
import { FiltersProvider, useGeneral, useUser } from '@contexts'
import { RoleEnum, StatusTicketEnum } from '@types'
import { ROUTE_KEYS } from '@constants'
import { ImplementationTimeUtils } from '@utils'

import { CertifiedAdmin } from '../../components/BatchesCertifiedAdmin'
import { ClosedAdmin } from '../../components/BatchesClosedAdmin'
import { OpenAdmin } from '../../components/BatchesOpenAdmin'
import { Settings } from '../../components/SettingsDashboard'
import { VehiclesSettings } from '../../components/VehiclesSettings'
import { Simulation } from '../../components/SettingsDashboard/simulation'
import DashboardRouter from '../../components/DashboardRouter'
import TicketView from '../../components/TicketView'
import { AdminRoRouter } from '../AdminRoRouter'
import { AdminRouter } from '../AdminRouter'
import AdminEVouchers from '../AdminRouter/AdminEVouchers'
import { LoginLayout } from '../Login'
import { ChangePassword } from '../Login/ChangePassword'
import { Login, LoginError } from '../Login/Login'
import { LoginSSO } from '../Login/LoginSSO'
import { LoginTraditional } from '../Login/LoginTraditional'
import { RecoverPassword } from '../Login/RecoverPassword'
import { ProcurementRouter } from '../ProcurementRouter'
import { SetToken } from '../Saml/Login'
import { Logout } from '../Saml/Logout'
import { SuppliersRouter } from '../SuppliersRouter'
import TicketRouter from '../TicketRouter'
import { TplRouter } from '../TplRouter'
import { Ongoing } from '../AdminRouter/Requests/Ongoing'
import { SuppliersDashboard } from '../SuppliersRouter/SuppliersDashboard'
import { AddTruckDocumentation } from '../SuppliersRouter/AddTruckDocumentation'
import { NewTicket } from '../SuppliersRouter/SuppliersDashboard/components/NewTicket'
import { TplDashboard } from '../TplRouter/TplDashboard'
import { TplDashboardBatches } from '../TplRouter/TplDashboardBatches'
import PrivateRoutes from '../../components/PrivateRoutes'
import AvailabilityRouter from '../TplRouter/AvailabilityRouter'
import VehiclesView from '../../components/VehiclesView'
import DriversView from '../../components/DriversView'
import TrailersView from '../../components/TrailersView'
import { FiltersProviderV2 } from '../../contexts/FiltersContextV2'
import CarriersView from '../../components/CarriersView'
import SuppliersView from '../../components/SuppliersView'
import { CommercialUsersView } from '../../components/CommercialUsersView'
import { ContractsDashboardSupplier } from '../../components/ContractsDashboardSupplier'
import { OrdersDashboard } from '../../components/OrdersDashboard'
import { WeeklyDashboard } from '../AdminRouter/AdminDashboardMetrics/WeeklyDashboard'
import { MonthlyDashboard } from '../AdminRouter/AdminDashboardMetrics/MonthlyDashboard'
import { CarrierQuotaConfigView } from '../AdminRouter/CarrierQuotaConfigView'

const { LOGIN, TOKEN, LOGOUT, ROOT, ADMINRO } = ROUTE_KEYS

const { PENDING, CREATED, CONFIRMED, TRANSIT, DELIVERED } = StatusTicketEnum

const { companyStartOfIsoWeek, companyStartOfToday, companyEndOfDay } = ImplementationTimeUtils

const AppRoutes = () => {
  const { maxDaysRange } = useGeneral()
  const { state: user } = useUser()
  const gmtZone = user?.company?.gmtZone ?? 0
  const companyMonday = companyStartOfIsoWeek(dayjs(), gmtZone).toDate()
  const companyTodayStart = companyStartOfToday(gmtZone)
  return (
    <Routes>
      <Route path={ROOT}>
        <Route index element={<Navigate to={LOGIN.HOME} />} />
        <Route path="login" element={<LoginLayout />}>
          <Route index element={<Login />} />
          <Route path={LOGIN.TRADITIONAL} element={<LoginTraditional />} />
          <Route path={LOGIN.SSO} element={<LoginSSO />} />
          <Route path={LOGIN.RECOVER} element={<RecoverPassword />} />
          <Route path={LOGIN.CHANGE_PASSWORD} element={<ChangePassword />} />
          <Route path={LOGIN.LOGIN_ERROR} element={<LoginError />} />
        </Route>
        <Route path={TOKEN} element={<SetToken />} />
        <Route path={LOGOUT} element={<Logout />} />
        <Route
          path="dashboard"
          element={user ? <DashboardRouter /> : <Navigate to={`/${LOGIN.HOME}`} />}
        >
          <Route
            path="suppliers"
            element={
              <PrivateRoutes allowedRoles={[RoleEnum.SUPPLIER]}>
                <SuppliersRouter />
              </PrivateRoutes>
            }
          >
            <Route
              index
              element={
                <FiltersProvider
                  key="filtersSupplierIndex"
                  customized={{
                    mondayOfWeek: companyMonday,
                  }}
                >
                  <SuppliersDashboard />
                </FiltersProvider>
              }
            />
            <Route
              path="previous"
              element={
                <FiltersProvider
                  key="filtersSupplierPrevious"
                  customized={{
                    dateFrom: companyTodayStart.subtract(maxDaysRange + 1, 'd').toDate(),
                    dateTo: companyEndOfDay(companyTodayStart, gmtZone).toDate(),
                    previous: true,
                  }}
                >
                  <SuppliersDashboard />
                </FiltersProvider>
              }
            />
            <Route path="truck-documentation" element={<AddTruckDocumentation />} />
            <Route path="new-ticket" element={<NewTicket />} />
          </Route>
          <Route
            path="commercial"
            element={
              <PrivateRoutes allowedRoles={[RoleEnum.COMMERCIAL]}>
                <SuppliersRouter />
              </PrivateRoutes>
            }
          >
            <Route
              index
              element={
                <FiltersProvider
                  key="filtersCommercialIndex"
                  customized={{
                    mondayOfWeek: companyMonday,
                  }}
                >
                  <SuppliersDashboard />
                </FiltersProvider>
              }
            />
            <Route
              path="previous"
              element={
                <FiltersProvider
                  key="filtersCommercialPrevious"
                  customized={{
                    dateFrom: companyTodayStart.subtract(maxDaysRange + 1, 'd').toDate(),
                    dateTo: companyEndOfDay(companyTodayStart, gmtZone).toDate(),
                    previous: true,
                  }}
                >
                  <SuppliersDashboard />
                </FiltersProvider>
              }
            />
            <Route path="truck-documentation" element={<AddTruckDocumentation />} />
            <Route path="new-ticket" element={<NewTicket />} />
          </Route>
          <Route
            path="carrier"
            element={
              <PrivateRoutes allowedRoles={[RoleEnum.CARRIER]}>
                <TplRouter />
              </PrivateRoutes>
            }
          >
            <Route
              index
              element={
                <FiltersProvider
                  key="filtersCarrierIndex"
                  customized={{
                    statusName: [PENDING],
                  }}
                >
                  <TplDashboard />
                </FiltersProvider>
              }
            />
            <Route
              path="all"
              element={
                <FiltersProvider
                  key="filtersCarrierAll"
                  customized={{
                    dateTo: companyEndOfDay(
                      companyTodayStart.add(maxDaysRange, 'd'),
                      gmtZone,
                    ).toDate(),
                    dateFrom: companyTodayStart.toDate(),
                  }}
                >
                  <TplDashboard />
                </FiltersProvider>
              }
            />
            <Route
              path="evouchers"
              element={
                <FiltersProvider
                  key="filtersCarrierEvouchers"
                  customized={{
                    dateFrom: companyTodayStart.subtract(maxDaysRange + 1, 'd').toDate(),
                    dateTo: companyEndOfDay(companyTodayStart, gmtZone).toDate(),
                    evouchers: true,
                  }}
                >
                  <TplDashboard />
                </FiltersProvider>
              }
            />
            <Route path="vehicles" element={<TplDashboard />} />
            <Route path="drivers" element={<TplDashboard />} />
            <Route path="batchees" element={<TplDashboardBatches />} />
            <Route path="availability" element={<AvailabilityRouter />} />
          </Route>
          <Route
            path="procurement"
            element={
              <PrivateRoutes allowedRoles={[RoleEnum.PROCUREMENT]}>
                <ProcurementRouter />
              </PrivateRoutes>
            }
          >
            <Route index element={<Navigate to="routes" />} />
            <Route path="routes" element={<RoutesDashboard />} />
            <Route path="contracts" element={<ContractsDashboard />} />
            <Route path="settings" element={<Settings />} />
            <Route path="simulation" element={<Simulation />} />
            <Route
              path="vehicles"
              element={
                <FiltersProviderV2 key="filtersAdminVehicles" customized={{ disabled: false }}>
                  <VehiclesView />
                </FiltersProviderV2>
              }
            />
            <Route
              path="drivers"
              element={
                <FiltersProviderV2 key="filtersAdminDrivers" customized={{ disabled: false }}>
                  <DriversView />
                </FiltersProviderV2>
              }
            />
            <Route
              path="trailers"
              element={
                <FiltersProviderV2 key="filtersAdminTrailers" customized={{ disabled: false }}>
                  <TrailersView />
                </FiltersProviderV2>
              }
            />
            <Route
              path="carriers"
              element={
                <FiltersProviderV2
                  key="filtersAdminCarriers"
                  customized={{ limit: 20, disabled: false }}
                >
                  <CarriersView />
                </FiltersProviderV2>
              }
            />
            <Route
              path="suppliers"
              element={
                <FiltersProviderV2
                  key="filtersAdminSuppliers"
                  customized={{ limit: 10, disabled: false }}
                >
                  <SuppliersView />
                </FiltersProviderV2>
              }
            />
            <Route path="metrics">
              <Route index element={<Navigate to="weekly" replace />} />
              <Route path="weekly" element={<WeeklyDashboard />} />
              <Route path="monthly" element={<MonthlyDashboard />} />
            </Route>
          </Route>
          <Route
            path="admin"
            element={
              <PrivateRoutes allowedRoles={[RoleEnum.ADMIN]}>
                <AdminRouter />
              </PrivateRoutes>
            }
          >
            <Route index element={<Navigate to="./ongoing" />} />
            <Route path="metrics">
              <Route index element={<Navigate to="weekly" replace />} />
              <Route path="weekly" element={<WeeklyDashboard />} />
              <Route path="monthly" element={<MonthlyDashboard />} />
            </Route>
            <Route
              path="ongoing"
              element={
                <FiltersProvider
                  key="filtersAdminOngoing"
                  customized={{
                    mondayOfWeek: companyMonday,
                    statusName: [PENDING, CREATED, CONFIRMED, TRANSIT, DELIVERED],
                  }}
                >
                  <Ongoing />
                </FiltersProvider>
              }
            />
            <Route
              path="orders"
              element={
                <FiltersProviderV2 key="filtersAdminOrders">
                  <OrdersDashboard />
                </FiltersProviderV2>
              }
            />
            <Route
              path="evouchers"
              element={
                <FiltersProvider
                  key="filtersAdminEvouchers"
                  customized={{
                    dateFrom: companyTodayStart.subtract(maxDaysRange + 1, 'd').toDate(),
                    dateTo: companyEndOfDay(companyTodayStart.add(1, 'd'), gmtZone).toDate(),
                    evouchers: true,
                  }}
                >
                  <AdminEVouchers />
                </FiltersProvider>
              }
            />
            <Route
              path="routes"
              element={
                <FiltersProvider key="filtersAdminRoutes">
                  <RoutesDashboard />
                </FiltersProvider>
              }
            />
            <Route path="ticket/:id" element={<TicketView />} />
            <Route
              path="sap-item-supplier"
              element={
                <FiltersProviderV2 key="filtersAdminSapItemSupplier">
                  <ContractsDashboardSupplier />
                </FiltersProviderV2>
              }
            />
            <Route path="contracts" element={<ContractsDashboard />} />
            <Route path="settings" element={<Settings />} />
            <Route path="vehicle-settings" element={<VehiclesSettings />} />
            <Route path="simulation" element={<Simulation />} />
            <Route path="open" element={<OpenAdmin />} />
            <Route path="closed" element={<ClosedAdmin />} />
            <Route path="certified" element={<CertifiedAdmin />} />
            <Route
              path="vehicles"
              element={
                <FiltersProviderV2 key="filtersAdminVehicles" customized={{ disabled: false }}>
                  <VehiclesView />
                </FiltersProviderV2>
              }
            />
            <Route
              path="drivers"
              element={
                <FiltersProviderV2 key="filtersAdminDrivers" customized={{ disabled: false }}>
                  <DriversView />
                </FiltersProviderV2>
              }
            />
            <Route
              path="trailers"
              element={
                <FiltersProviderV2 key="filtersAdminTrailers" customized={{ disabled: false }}>
                  <TrailersView />
                </FiltersProviderV2>
              }
            />
            <Route
              path="carriers"
              element={
                <FiltersProviderV2
                  key="filtersAdminCarriers"
                  customized={{ limit: 20, disabled: false }}
                >
                  <CarriersView />
                </FiltersProviderV2>
              }
            />
            <Route path="carrier-quotas" element={<CarrierQuotaConfigView />} />
            <Route
              path="suppliers"
              element={
                <FiltersProviderV2
                  key="filtersAdminSuppliers"
                  customized={{ limit: 10, disabled: false }}
                >
                  <SuppliersView />
                </FiltersProviderV2>
              }
            />
            <Route
              path="commercial"
              element={
                <FiltersProviderV2 key="filtersAdminCommercial" customized={{ limit: 10 }}>
                  <CommercialUsersView />
                </FiltersProviderV2>
              }
            />
          </Route>
          <Route
            path={ADMINRO.HOME}
            element={
              <PrivateRoutes allowedRoles={[RoleEnum.ADMINRO]}>
                <AdminRoRouter />
              </PrivateRoutes>
            }
          >
            <Route index element={<Navigate to="./ongoing" />} />
            <Route
              path="ongoing"
              element={
                <FiltersProvider
                  key="filtersAdminROOngoing"
                  customized={{
                    mondayOfWeek: companyMonday,
                    statusName: [PENDING, CREATED, CONFIRMED, TRANSIT, DELIVERED],
                  }}
                >
                  <Ongoing />
                </FiltersProvider>
              }
            />
            <Route
              path="orders"
              element={
                <FiltersProviderV2 key="filtersAdminROOrders">
                  <OrdersDashboard />
                </FiltersProviderV2>
              }
            />
            <Route
              path="evouchers"
              element={
                <FiltersProvider
                  key="filtersAdminROEvouchers"
                  customized={{
                    dateFrom: companyTodayStart.subtract(maxDaysRange + 1, 'd').toDate(),
                    dateTo: companyEndOfDay(companyTodayStart.add(1, 'd'), gmtZone).toDate(),
                    evouchers: true,
                  }}
                >
                  <AdminEVouchers />
                </FiltersProvider>
              }
            />
            <Route
              path="routes"
              element={
                <FiltersProvider key="filtersAdminRORoutes">
                  <RoutesDashboard />
                </FiltersProvider>
              }
            />
            <Route path="ticket/:id" element={<TicketView />} />
            <Route
              path="sap-item-supplier"
              element={
                <FiltersProviderV2 key="filtersAdminROSapItemSupplier">
                  <ContractsDashboardSupplier />
                </FiltersProviderV2>
              }
            />
            <Route path="contracts" element={<ContractsDashboard />} />
            <Route path="settings" element={<Settings />} />
            <Route path="simulation" element={<Simulation />} />
            <Route path="open" element={<OpenAdmin />} />
            <Route path="closed" element={<ClosedAdmin />} />
            <Route path="certified" element={<CertifiedAdmin />} />
            <Route
              path="vehicles"
              element={
                <FiltersProviderV2 key="filtersAdminROVehicles" customized={{ disabled: false }}>
                  <VehiclesView />
                </FiltersProviderV2>
              }
            />
            <Route
              path="drivers"
              element={
                <FiltersProviderV2 key="filtersAdminRODrivers" customized={{ disabled: false }}>
                  <DriversView />
                </FiltersProviderV2>
              }
            />
            <Route
              path="trailers"
              element={
                <FiltersProviderV2 key="filtersAdminROTrailers" customized={{ disabled: false }}>
                  <TrailersView />
                </FiltersProviderV2>
              }
            />
            <Route
              path="carriers"
              element={
                <FiltersProviderV2 key="filtersAdminROCarriers" customized={{ limit: 20 }}>
                  <CarriersView />
                </FiltersProviderV2>
              }
            />
            <Route
              path="suppliers"
              element={
                <FiltersProviderV2 key="filtersAdminSuppliers" customized={{ limit: 10 }}>
                  <SuppliersView />
                </FiltersProviderV2>
              }
            />
          </Route>
          <Route
            path="ticket/:id"
            element={
              <PrivateRoutes allowedRoles={[RoleEnum.ADMIN, RoleEnum.ADMINRO]}>
                <TicketRouter />
              </PrivateRoutes>
            }
          />
        </Route>
      </Route>
    </Routes>
  )
}

export default AppRoutes
